import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { PlusIcon } from "@radix-ui/react-icons";
import { useParams } from "react-router-dom";
import { TLAssetId, TLShapeId, useEditor } from "@tldraw/tldraw";
import SearchIcon from "pages/flyer/builder/drawer/components/Primitives/icons/SearchIcon";
import { PossibleFlyerType, TemplateConfig } from "pages/flyer/builder/drawer/types";
import { FORMATS } from "pages/flyer/builder/constants";
import { useStore } from "store";
import { useBrandKitStore } from "store/brandKit";
import { uploadBrandKitAsset } from "pages/flyer/builder/services/brandKitUploadService";
import { useImageUpload } from "pages/flyer/builder/drawer/contexts/ImageUploadContext";
import { SnackBarContext } from "components/snack-bar";
import CONFIG from "../../../../../../../../config/config";
import { useTldrawApp } from "pages/flyer/builder/drawer/hooks";
import InputFilter from "./InputFilter";
import Item from "./Item";

interface ImageComponentProps {
  searchOnEnter?: boolean;
}

const ImageComponent = ({ searchOnEnter = false }: ImageComponentProps) => {
  const { format } = useParams();
  const { imageState, setImageState } = useStore();

  const editor = useEditor();
  const app = useTldrawApp();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [query, setQuery] = useState<string>("");
  const [search, setSearch] = useState<string>("");
  const [orientation, setOrientation] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [photos, setPhotos] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [fromBrandKit, setFromBrandKit] = useState(false);

  const currentFormatConfig = FORMATS[format as PossibleFlyerType];

  const fetchPhotos = async (query: string, page: number) => {
    if (loading) return; // Prevent multiple fetches
    setLoading(true);
    try {
      const response = await fetch(
        `${CONFIG.UNSPLASH.unsplashUrl}/search/photos?client_id=${
          CONFIG.UNSPLASH.unsplashAccessKey
        }&page=${page}&query=${query}${orientation ? `&orientation=${orientation}` : ""}&per_page=30`
      );

      const { results, total_pages } = await response.json();
      if (page === 1) {
        setPhotos(results);
      } else {
        setPhotos((prevPhotos) => [...prevPhotos, ...results]);
      }
      setHasMore(page < total_pages);
    } catch (error) {
      console.error("Error fetching photos from Unsplash:", error);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  const checkAndFetchMorePhotos = (query: string) => {
    const container = document.querySelector(".photos-container");
    if (container && container.scrollHeight <= container.clientHeight && hasMore) {
      setCurrentPage((prevPage) => {
        const nextPage = prevPage + 1;
        fetchPhotos(query, nextPage).then(() => {
          checkAndFetchMorePhotos(query); // Check again after fetching
        });
        return nextPage;
      });
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      const query = event.currentTarget.value.trim();
      if (query) {
        setPhotos([]);
        setCurrentPage(1);
        setSearch(query);
        setQuery(query);

        fetchPhotos(query, 1).then(() => {
          checkAndFetchMorePhotos(query);
        });
      }
    }
  };

  const handleScroll = () => {
    const container = document.querySelector(".photos-container");
    if (
      container &&
      container.scrollHeight - container.scrollTop <= container.clientHeight + 50 &&
      !loading &&
      hasMore
    ) {
      setCurrentPage((prevPage) => {
        const nextPage = prevPage + 1;
        fetchPhotos(search || "nature", nextPage);
        return nextPage;
      });
    }
  };

  useEffect(() => {
    const container = document.querySelector(".photos-container");
    if (container) {
      container.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (container) {
        container.removeEventListener("scroll", handleScroll);
      }
    };
  }, [search, loading, hasMore]);

  // On mount: check for external search from Brand Kit, otherwise default auto-search
  const isInitialMountRef = useRef(true);
  useEffect(() => {
    if (imageState.search) {
      const q = imageState.search;
      setQuery(q);
      setSearch(q);
      setFromBrandKit(true);
      fetchPhotos(q, 1);
      setImageState({ search: "" });
      return;
    }
    if (!searchOnEnter && currentPage === 1 && photos.length === 0 && !search) {
      const defaultQuery = "nature";
      setSearch(defaultQuery);
      fetchPhotos(defaultQuery, 1);
    }
  }, []);

  // Watch for external search changes when Image is already mounted
  useEffect(() => {
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      return;
    }
    if (imageState.search) {
      const q = imageState.search;
      setQuery(q);
      setSearch(q);
      setFromBrandKit(true);
      setPhotos([]);
      setCurrentPage(1);
      fetchPhotos(q, 1);
      setImageState({ search: "" });
    }
  }, [imageState.search]);

  // Re-fetch the current query whenever the orientation filter changes so
  // the active result list reflects the new filter (Square / Portrait /
  // Landscape / cleared). Without this the orientation only applied to
  // subsequent searches, and the current grid stayed unfiltered until the
  // user hit Enter again.
  const isOrientationInitialRef = useRef(true);
  useEffect(() => {
    if (isOrientationInitialRef.current) {
      isOrientationInitialRef.current = false;
      return;
    }
    setPhotos([]);
    setCurrentPage(1);
    fetchPhotos(search || "nature", 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orientation]);

  const { startUpload, endUpload } = useImageUpload();
  const runSnackBar = useContext(SnackBarContext);

  // Save Unsplash image to Brand Kit (fetch blob → upload to S3 → save metadata)
  const handleSaveToBrandKit = useCallback(
    async (photoUrl: string, photoName: string) => {
      startUpload("Saving image to Brand Kit...");
      try {
        const response = await fetch(photoUrl);
        const blob = await response.blob();
        const ext = blob.type.split("/")[1] || "jpg";
        const file = new File([blob], `${photoName}.${ext}`, { type: blob.type || "image/jpeg" });
        await uploadBrandKitAsset(file, "image", photoName);
        useBrandKitStore.getState().loadBrandKit();
      } catch (err) {
        console.error("Failed to save Unsplash image to Brand Kit:", err);
        runSnackBar({
          msg: "Failed to save image. Please try again.",
          type: "error",
          vertical: "top",
          horizontal: "center",
        });
      } finally {
        endUpload();
      }
    },
    [startUpload, endUpload]
  );

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!editor || !event.target.files?.[0]) return;

    const file = event.target.files[0];

    try {
      // Use onAssetCreate callback if available (handles S3 upload for large files)
      let src: string;
      if (app.callbacks.onAssetCreate) {
        const result = await app.callbacks.onAssetCreate(file, `temp-${Date.now()}`);
        if (!result) {
          return;
        }
        src = result;
      } else {
        // Fallback to base64 if no callback
        const reader = new FileReader();
        src = await new Promise<string>((resolve, reject) => {
          reader.onload = (e: any) => resolve(e.target.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      }

      const tempUrl = URL.createObjectURL(file);
      const img = document.createElement("img");
      img.src = tempUrl;

      img.onload = () => {
        let imageWidth = img.width;
        let imageHeight = img.height;
        const aspectRatio = imageWidth / imageHeight;
        const orientation = aspectRatio > 1 ? "landscape" : "portrait";
        const assetId = `asset:${file.name}-${Date.now()}`;

        if (orientation === "landscape") {
          imageWidth = currentFormatConfig?.displayWidth || 100;
          imageHeight = imageWidth / aspectRatio;
        } else {
          imageHeight = currentFormatConfig?.displayHeight || 100;
          imageWidth = imageHeight * aspectRatio;
        }

        const { width, height } = editor.getViewportScreenBounds();
        const center = editor.screenToPage({ x: width / 2, y: height / 2 });

        editor.createAssets([
          {
            id: assetId as TLAssetId,
            type: "image",
            typeName: "asset",
            props: {
              name: file.name,
              src: src,
              w: imageWidth,
              h: imageHeight,
              mimeType: file.type,
              isAnimated: false,
            },
            meta: {
              name: file.name,
            },
          },
        ]);

        const shapeId = `shape:${file.name}-${Date.now()}` as TLShapeId;
        editor.createShapes([
          {
            id: shapeId,
            type: "customImage",
            props: {
              assetId,
              w: imageWidth,
              h: imageHeight,
            },
            x: center.x - imageWidth / 2,
            y: center.y - imageHeight / 2,
          },
        ]);

        // Switch to select tool and select the newly created image
        editor.setCurrentTool("select");
        editor.select(shapeId);

        URL.revokeObjectURL(tempUrl);
        // Reset the file input to allow re-uploading the same file
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      };
    } catch (error) {
      console.error("❌ [Image Component] Error processing image:", error);
    }
  };

  // Reset search when image is selected in edit mode
  const handleImageSelected = () => {
    if (searchOnEnter) {
      setQuery("");
      setSearch("");
      setPhotos([]);
      setCurrentPage(1);
      setHasMore(true);
    }
  };

  return (
    <div className={`flex flex-col ${searchOnEnter && !search ? "" : "h-full"} bg-white w-[240px] pl-4 pr-4`}>
      <div className={`flex gap-2 items-center mt-2 ${search || photos.length > 0 ? "mb-4" : ""}`}>
        <div className="relative w-full flex items-center">
          <div className="absolute top-1/2 -translate-y-1/2 left-1">
            <SearchIcon />
          </div>
          <input
            className="w-full h-[32px] border border-[#E2E2FC] rounded-[6px] pl-[24px] py-[3px] placeholder:[#707087] hover:shadow-custom transition-all"
            placeholder="Search images here"
            onKeyDown={handleKeyDown}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <InputFilter
            setPhotos={(photos) => setPhotos(photos)}
            setPage={(page) => setCurrentPage(page)}
            setOrientation={(orientation) => setOrientation(orientation)}
            setAnchorEl={setAnchorEl}
            anchorEl={anchorEl}
            orientation={orientation}
          />
        </div>
        <button
          type="button"
          style={{ border: "1px solid #E2E2FC", width: isHovered ? "150px" : "27px" }}
          className="w-[25px] h-[25px] p-1 rounded-[6px] flex items-center transition-all duration-300 overflow-hidden hover:shadow-custom"
          onClick={() => fileInputRef.current?.click()}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <PlusIcon />
          <span
            className={`ml-1 text-nowrap !leading-1 transition-opacity duration-300 ${
              isHovered ? "opacity-100" : "opacity-0"
            }`}
          >
            New image
          </span>
        </button>
        <input type="file" ref={fileInputRef} style={{ display: "none" }} onChange={handleFileUpload} />
      </div>
      {/* Only show the container of photos if there is a search performed or if searchOnEnter is not active */}
      {(!searchOnEnter || search) && (
        <ul className="grid grid-cols-2 gap-3 pt-1 photos-container overflow-y-auto">
          {photos.map((photo: any, index: number) => (
            <li
              key={`${photo.id}-${index}`}
              className="rounded-[8px] bg-slate-100 overflow-hidden w-[94px] h-[94px] hover:shadow-[0px_2px_10px_2px_rgba(0,0,0,_0.1)] transition-all duration-300"
            >
              <Item
                format={currentFormatConfig}
                key={photo.id}
                photo={photo}
                editor={editor}
                onImageSelected={searchOnEnter ? handleImageSelected : undefined}
                saveToBrandKit={fromBrandKit ? handleSaveToBrandKit : undefined}
              />
            </li>
          ))}
        </ul>
      )}
      {loading && <p>Loading...</p>}
    </div>
  );
};

export const Image = React.memo(ImageComponent);
